import { Button, Input, Segmented, Table, Tag, Tooltip } from "antd";
import {
	CheckCircleOutlined,
	CloseCircleOutlined,
	ReloadOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Navigate } from "react-router-dom";
import PageTitle from "../page-header/PageHeader";
import {
	getResignationList,
	reviewResignation,
} from "../../redux/rtk/features/resignation/resignationSlice";
import getRoleIdFromToken from "../../utils/getRoleIdByToken";
import getUserFromToken from "../../utils/getUserFromToken";

const ROLE_ID = { ADMIN: 1, HR: 2, MANAGER: 3, TEAM_LEAD: 4, EMPLOYEE: 5 };

const RESIGNATION_REASONS = [
	{ value: "better_opportunity", label: "Better opportunity" },
	{ value: "personal", label: "Personal" },
	{ value: "relocation", label: "Relocation" },
	{ value: "retirement", label: "Retirement" },
	{ value: "health", label: "Health" },
	{ value: "other", label: "Other" },
];

const getReasonLabel = (value) =>
	RESIGNATION_REASONS.find((r) => r.value === value)?.label || value || "—";

const statusTag = (status) => {
	// 1 = approve, 2 = reject, 0 = pending; API may return number, string, or object
	if (typeof status === "number") {
		if (status === 1) return <Tag color="green">Approved</Tag>;
		if (status === 2) return <Tag color="red">Rejected</Tag>;
		return <Tag color="orange">Pending</Tag>;
	}
	const str = typeof status === "string" ? status : status != null ? String(status) : "";
	const s = str.toUpperCase();
	if (s === "APPROVED" || s === "1") return <Tag color="green">Approved</Tag>;
	if (s === "REJECTED" || s === "2") return <Tag color="red">Rejected</Tag>;
	return <Tag color="orange">Pending</Tag>;
};

export default function ResignationApprovals() {
	const dispatch = useDispatch();
	const [status, setStatus] = useState("0");
	const [searchEmployee, setSearchEmployee] = useState("");
	const isLogged = Boolean(localStorage.getItem("isLogged"));

	const { list, loading } = useSelector((state) => state.resignation);
	const roleId = getRoleIdFromToken();
	const currentEmployeeId = getUserFromToken(); // EmployeeId of logged-in user (saved as HRId when HR approves/rejects)

	useEffect(() => {
		dispatch(getResignationList({ page: 1, limit: 50, status }));
	}, [dispatch, status]);

	const role = roleId != null ? Number(roleId) : null;
	const isAdmin = role === ROLE_ID.ADMIN;
	const isManager = role === ROLE_ID.MANAGER;
	const isHR = role === ROLE_ID.HR;
	const canActAsApprover = isAdmin || isManager || isHR;
	// If role missing from token, show actions so user can still approve/reject (treat as can act)
	const showActionsColumn = canActAsApprover;

	const handleApprove = (record, approvalFor) => {
		if (!approvalFor) return;
		dispatch(
			reviewResignation({
				id: getResignationId(record),
				record,
				approvalFor,
				approved: true,
				reviewerEmployeeId: currentEmployeeId ? Number(currentEmployeeId) : undefined,
			})
		).then(() => {
			dispatch(getResignationList({ page: 1, limit: 50, status }));
		});
	};

	const handleReject = (record, approvalFor) => {
		if (!approvalFor) return;
		dispatch(
			reviewResignation({
				id: getResignationId(record),
				record,
				approvalFor,
				approved: false,
				reviewerEmployeeId: currentEmployeeId ? Number(currentEmployeeId) : undefined,
			})
		).then(() => {
			dispatch(getResignationList({ page: 1, limit: 50, status }));
		});
	};

	const getResignationId = (record) =>
		record.id ?? record.ResignationId ?? record.resignationId;

	const renderManagerCell = (_, record) =>
		statusTag(record.ManagerApproval ?? record.managerApproval ?? record.ManagerApprovalStatus);

	const renderHRCell = (_, record) =>
		statusTag(record.HRApproval ?? record.hrApproval ?? record.HRApprovalStatus);

	// Helper to get status code: 0=Pending, 1=Approved, 2=Rejected
	const getRecordStatus = (record) => {
		const m = record.ManagerApproval ?? record.managerApproval ?? record.ManagerApprovalStatus;
		const h = record.HRApproval ?? record.hrApproval ?? record.HRApprovalStatus;
		const managerRejected = m === 2 || String(m).toUpperCase() === "REJECTED" || String(m) === "2";
		const hrRejected = h === 2 || String(h).toUpperCase() === "REJECTED" || String(h) === "2";
		const bothApproved = (m === 1 || String(m).toUpperCase() === "APPROVED") && (h === 1 || String(h).toUpperCase() === "APPROVED");

		if (managerRejected || hrRejected) return 2;
		if (bothApproved) return 1;
		return 0;
	};

	// Overall: Approved only when both HR and Manager approved; else Pending (or Rejected if either rejected).
	const overallStatusTag = (record) => {
		const status = getRecordStatus(record);
		return statusTag(status);
	};

	const baseColumns = [
		{
			title: "Employee",
			dataIndex: "employeeName",
			key: "employeeName",
			render: (_, record) =>
				record.employeeName ||
				record.EmployeeName ||
				record.userName ||
				record.UserName ||
				"—",
		},
		{
			title: "Notice period start",
			key: "noticePeriodStartDate",
			dataIndex: "NoticePeriodStartDate",
			render: (val, record) => {
				const d =
					val ||
					record.noticePeriodStartDate ||
					record.NoticePeriodStartDate;
				return d ? dayjs(d).format("DD MMM YYYY") : "—";
			},
		},
		{
			title: "Last working date",
			dataIndex: "lastWorkingDate",
			key: "lastWorkingDate",
			render: (val, record) => {
				const d =
					val ||
					record.LastWorkingDay ||
					record.LastWorkingDate ||
					record.last_working_date;
				return d ? dayjs(d).format("DD MMM YYYY") : "—";
			},
		},
		{
			title: "Resignation reason",
			key: "resignationReason",
			render: (_, record) => {
				const raw =
					record.ResignationReason ??
					record.resignationReason ??
					record.reason ??
					record.Reason ??
					record.resignationReasonName ??
					"";
				return raw ? getReasonLabel(raw) || raw : "—";
			},
		},
		{
			title: "Submitted",
			dataIndex: "submittedAt",
			key: "submittedAt",
			render: (val, record) => {
				const d =
					val ||
					record.SubmittedAt ||
					record.createdAt ||
					record.CreatedAt;
				return d ? dayjs(d).format("DD MMM YYYY") : "—";
			},
		},
	];

	const managerColumn = isManager || isAdmin
		? [
				{
					title: "Manager",
					key: "managerStatus",
					render: renderManagerCell,
				},
			]
		: [];

	const hrColumn = isHR || isAdmin
		? [
				{
					title: "HR",
					key: "hrStatus",
					render: renderHRCell,
				},
			]
		: [];

	const getApprovalFor = () => {
		if (isAdmin) return "admin";
		if (isManager) return "manager";
		if (isHR) return "hr";
		return null;
	};

	const renderActionsCell = (_, record) => {
		const managerApproval = record.ManagerApproval ?? record.managerApproval ?? record.ManagerApprovalStatus ?? 0;
		const hrApproval = record.HRApproval ?? record.hrApproval ?? record.HRApprovalStatus ?? 0;
		const managerPending = managerApproval !== 1 && managerApproval !== 2 && String(managerApproval) !== "1" && String(managerApproval) !== "2";
		const hrPending = hrApproval !== 1 && hrApproval !== 2 && String(hrApproval) !== "1" && String(hrApproval) !== "2";

		const canActManager = (isManager || isAdmin) && managerPending;
		const canActHR = (isHR || isAdmin) && hrPending;
		const canAct = canActManager || canActHR;
		const approvalFor = getApprovalFor();

		if (!canAct || !approvalFor) return null;

		return (
			<div style={{ display: "flex", alignItems: "center", gap: 8 }}>
				<Tooltip title="Approve">
					<Button
						size="small"
						icon={<CheckCircleOutlined />}
						onClick={() => handleApprove(record, approvalFor)}
						style={{ color: "#fff", borderColor: "#52c41a", backgroundColor: "#52c41a" }}
						className="approve-btn"
					/>
				</Tooltip>
				<Tooltip title="Reject">
					<Button
						size="small"
						icon={<CloseCircleOutlined />}
						onClick={() => handleReject(record, approvalFor)}
						style={{ color: "#fff", borderColor: "#ff4d4f", backgroundColor: "#ff4d4f" }}
						className="reject-btn"
					/>
				</Tooltip>
			</div>
		);
	};

	const columns = [
		...baseColumns,
		...managerColumn,
		...hrColumn,
		{
			title: "Overall",
			key: "overallStatus",
			render: (_, record) => overallStatusTag(record),
		},
		...(showActionsColumn
			? [
					{
						title: "Actions",
						key: "action",
						render: renderActionsCell,
						width: 100,
					},
				]
			: []),
	];

	const addKeys = (arr) =>
		(Array.isArray(arr) ? arr : []).map((item, index) => ({
			...item,
			key: getResignationId(item) || index,
		}));

	const filteredData = addKeys(list).filter((item) => {
		const name = (
			item.employeeName ||
			item.EmployeeName ||
			item.userName ||
			item.UserName ||
			""
		).toLowerCase();
		const nameMatch = name.includes(searchEmployee.toLowerCase());
		const statusMatch = getRecordStatus(item) === parseInt(status, 10);
		return nameMatch && statusMatch;
	});

	if (!isLogged) {
		return <Navigate to="/admin/auth/login" replace />;
	}

	return (
		<>
			<div className="flex my-2 justify-between mb-4 flex-wrap gap-2">
				<Input.Search
					placeholder="Search by employee name"
					allowClear
					onSearch={setSearchEmployee}
					onChange={(e) => setSearchEmployee(e.target.value)}
					style={{ width: 260 }}
				/>
				<div className="flex justify-end gap-2">
					<Button
						icon={<ReloadOutlined />}
						onClick={() =>
							dispatch(
								getResignationList({ page: 1, limit: 50, status })
							)
						}
						type="default"
					>
						Refresh
					</Button>
					<Segmented
						value={status}
						onChange={setStatus}
						options={[
							{ label: "Pending", value: "0" },
							{ label: "Approved", value: "1" },
							{ label: "Rejected", value: "2" },
						]}
					/>
				</div>
			</div>

			<Table
				columns={columns}
				dataSource={filteredData}
				loading={loading}
				pagination={{ pageSize: 10 }}
				scroll={{ x: true }}
			/>
		</>
	);
}
